import { getSdk, markCacheDirty } from "@/composables/useSdk";
import { useAuthStorage } from "@/composables/useAuthStorage";
import { resolveApiBaseUrl } from "@/utils/env";
import type { AuthUser, AuthTokens } from "@/types";
import { mapCurrentUserToAuthUser } from "@/services/authUserProfile";

// Storage keys
const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "auth_user";

class AuthService {
  private parseAuthTokens(payload: unknown): AuthTokens | null {
    if (!payload || typeof payload !== "object") {
      return null;
    }

    const tokenPayload =
      "data" in payload &&
      payload.data &&
      typeof payload.data === "object"
        ? payload.data
        : payload;

    const accessToken =
      "access_token" in tokenPayload
        ? tokenPayload.access_token
        : "accessToken" in tokenPayload
          ? tokenPayload.accessToken
          : undefined;
    const refreshToken =
      "refresh_token" in tokenPayload
        ? tokenPayload.refresh_token
        : "refreshToken" in tokenPayload
          ? tokenPayload.refreshToken
          : undefined;

    if (typeof accessToken !== "string" || accessToken.length === 0) {
      return null;
    }

    return {
      accessToken,
      refreshToken:
        typeof refreshToken === "string" && refreshToken.length > 0
          ? refreshToken
          : undefined,
    };
  }

  /**
   * Send auth code to email (magic link)
   */
  async sendMagicLink(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    const sdk = getSdk();
    await sdk.requestMagicLink(email, {
      referralCode: options?.referralCode || undefined,
      redirect: options?.redirect || undefined,
      platform: options?.platform,
    });
  }

  /**
   * Send auth code to email (alias for sendMagicLink with cleaner name)
   */
  async sendAuthCode(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    const sdk = getSdk();
    await sdk.requestAuthCode(email, {
      referralCode: options?.referralCode || undefined,
      redirect: options?.redirect || undefined,
      platform: options?.platform,
    });
  }

  /**
   * Verify the 6-digit code from magic link
   */
  async verifyCode(
    email: string,
    code: string,
  ): Promise<{ tokens: AuthTokens; user: AuthUser }> {
    const sdk = getSdk();

    // Use SDK to login with magic link
    const sdkTokens = await sdk.loginWithMagicLink(email, code);

    // Convert SDK tokens to our AuthTokens type
    const tokens: AuthTokens = {
      accessToken: sdkTokens.accessToken,
      refreshToken: sdkTokens.refreshToken,
    };

    // Fetch user profile using SDK
    const user = await this.fetchCurrentUser();

    return { tokens, user };
  }

  /**
   * Login with OAuth provider (Google, Apple, etc.)
   * @param provider - OAuth provider name (e.g., 'google', 'apple')
   * @param code - Authorization code from OAuth callback
   * @param redirectUri - The redirect URI used in the OAuth flow
   * @param extraData - Additional provider-specific data (e.g., Apple's id_token and user info)
   */
  async loginWithOAuth(
    provider: string,
    code: string,
    redirectUri?: string,
    extraData?: { id_token?: string | null; user?: string | null },
  ): Promise<{ tokens: AuthTokens; user: AuthUser }> {
    const sdk = getSdk();

    // Use SDK to exchange OAuth code for tokens
    const sdkTokens = await sdk.loginWithOAuth(provider, code, redirectUri, extraData);

    // Convert SDK tokens to our AuthTokens type
    const tokens: AuthTokens = {
      accessToken: sdkTokens.accessToken,
      refreshToken: sdkTokens.refreshToken,
    };

    // Fetch user profile using SDK
    const user = await this.fetchCurrentUser();

    return { tokens, user };
  }

  /**
   * Fetch current authenticated user
   */
  async fetchCurrentUser(): Promise<AuthUser> {
    const sdk = getSdk();
    const profile = await sdk.getCurrentUser();

    if (!profile) {
      throw new Error("Failed to fetch user profile");
    }
    const user = mapCurrentUserToAuthUser(profile);

    // Store user in localStorage
    localStorage.setItem(USER_KEY, JSON.stringify(user));

    return user;
  }

  /**
   * Store user data in localStorage (for read-after-write pattern)
   */
  storeUser(user: AuthUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Persist authentication tokens through the SDK session layer.
   */
  async setTokens(tokens: AuthTokens): Promise<void> {
    const sdk = getSdk();
    await sdk.setSession({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });
  }

  /**
   * Set developer token directly
   */
  async setDeveloperToken(token: string): Promise<void> {
    const sdk = getSdk();
    await sdk.setSession({ accessToken: token });
  }

  /**
   * Establish a normal cookie-backed web session from tokens delivered out-of-band
   * (for example Apple's form_post callback redirect).
   */
  async establishSession(tokens: AuthTokens): Promise<AuthTokens> {
    const sdk = getSdk();
    const authStorage = useAuthStorage();

    if (authStorage.isNative || !tokens.refreshToken) {
      await sdk.setSession(tokens);
      return tokens;
    }

    const baseUrl = resolveApiBaseUrl().replace(/\/+$/, "");
    const response = await fetch(`${baseUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      credentials: "include",
    });

    const responseText = await response.text();
    let payload: unknown = null;

    if (responseText) {
      try {
        payload = JSON.parse(responseText);
      } catch {
        payload = responseText;
      }
    }

    if (!response.ok) {
      const message =
        payload && typeof payload === "object" && "message" in payload
          ? String(payload.message)
          : response.statusText || "Failed to establish authenticated session";
      throw new Error(message);
    }

    const refreshedTokens = this.parseAuthTokens(payload);
    if (!refreshedTokens?.accessToken) {
      throw new Error("Invalid token response from /auth/refresh");
    }

    await sdk.setSession(refreshedTokens);
    return refreshedTokens;
  }

  /**
   * Get the currently active in-memory tokens, or fall back to any legacy
   * localStorage snapshot that hasn't been migrated away yet.
   */
  getTokens(): AuthTokens | null {
    const sdk = getSdk();
    const activeTokens = sdk.getTokens();
    if (activeTokens?.accessToken || activeTokens?.refreshToken) {
      return activeTokens;
    }

    const accessToken = localStorage.getItem(TOKEN_KEY) || undefined;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || undefined;
    if (!accessToken && !refreshToken) return null;

    return {
      accessToken,
      refreshToken,
    };
  }

  /**
   * Get stored tokens from the SDK-managed persisted session.
   */
  async getTokensAsync(): Promise<AuthTokens | null> {
    const sdk = getSdk();
    const restoredTokens = await sdk.restoreSession();
    return restoredTokens ?? this.getTokens();
  }

  /**
   * Get stored user
   */
  getStoredUser(): AuthUser | null {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get current user (alias for getStoredUser)
   */
  getUser(): AuthUser | null {
    return this.getStoredUser();
  }

  clearStoredUser(): void {
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return Boolean(this.getUser() || this.getTokens()?.accessToken);
  }

  /**
   * Logout - clear all auth data
   */
  async logout(): Promise<void> {
    const sdk = getSdk();

    this.clearStoredUser();

    try {
      const currentTokens = sdk.getTokens() ?? this.getTokens();
      if (currentTokens?.accessToken || currentTokens?.refreshToken) {
        await sdk.signOut();
      } else {
        await sdk.clearSession();
        await sdk.clearCache();
      }
    } catch (error) {
      console.warn("authService: logout failed; clearing local session and cache anyway", error);
      try {
        await sdk.clearSession();
      } catch (sessionError) {
        console.warn("authService: failed to clear SDK session on logout", sessionError);
      }
      try {
        await sdk.clearCache();
      } catch (cacheError) {
        console.warn("authService: failed to clear SDK cache on logout", cacheError);
        markCacheDirty();
      }
    }
  }

  /**
   * Legacy synchronous bootstrap snapshot used by older callers.
   */
  initializeFromStorage(): {
    tokens: AuthTokens | null;
    user: AuthUser | null;
  } {
    const tokens = this.getTokens();
    const user = this.getStoredUser();
    return { tokens, user };
  }

  /**
   * Restore persisted SDK session state and return the cached user snapshot.
   */
  async restoreSession(): Promise<{
    tokens: AuthTokens | null;
    user: AuthUser | null;
  }> {
    const sdk = getSdk();
    const tokens = await sdk.restoreSession();
    const user = this.getStoredUser();
    return { tokens, user };
  }
}

// Export singleton
export const authService = new AuthService();
export default authService;
